import { useState } from "react";
import { X, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateNotificationConfig } from "@/hooks/use-api";
import type { NotificationCategory } from "@/types/api";
import { CategoryMultiSelect } from "./category-multi-select";

interface AddNotificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddNotificationDialog({
  open,
  onOpenChange,
}: AddNotificationDialogProps) {
  const [channel, setChannel] = useState<"email" | "slack" | "webhook">("email");
  const [minSeverity, setMinSeverity] = useState("medium");
  const [configValue, setConfigValue] = useState("");
  const [categories, setCategories] = useState<NotificationCategory[]>([]);
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateNotificationConfig();

  function reset() {
    setChannel("email");
    setMinSeverity("medium");
    setConfigValue("");
    setCategories([]);
    setError(null);
  }

  function handleClose() {
    reset();
    onOpenChange(false);
  }

  async function handleSubmit() {
    if (!configValue.trim()) {
      setError("Configuration value is required");
      return;
    }

    const config: Record<string, unknown> = {};
    switch (channel) {
      case "email":
        config.recipients = configValue
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        break;
      case "slack":
        config.webhook_url = configValue.trim();
        break;
      case "webhook":
        config.url = configValue.trim();
        break;
    }

    setError(null);
    try {
      await createMutation.mutateAsync({
        channel,
        config,
        min_severity: minSeverity,
        category_filter: categories.length > 0 ? categories : undefined,
      });
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create notification",
      );
    }
  }

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Close add notification dialog"
        className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Add Notification Channel"
          className="pointer-events-auto relative w-full max-w-md rounded-lg border bg-background shadow-lg"
        >
          <div className="flex items-center justify-between border-b px-6 py-4">
            <h2 className="text-lg font-semibold">Add Notification Channel</h2>
            <button
              type="button"
              className="rounded-sm p-1 opacity-70 hover:opacity-100 transition-opacity"
              onClick={handleClose}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-4 p-6">
            <div>
              <Label htmlFor="channel">Channel Type</Label>
              <select
                id="channel"
                value={channel}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setChannel(e.target.value as "email" | "slack" | "webhook")
                }
                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="email">Email</option>
                <option value="slack">Slack</option>
                <option value="webhook">Webhook</option>
              </select>
            </div>

            <div>
              <Label htmlFor="config-value">
                {channel === "email"
                  ? "Recipients (comma separated)"
                  : channel === "slack"
                    ? "Slack Webhook URL"
                    : "Webhook URL"}
              </Label>
              <Input
                id="config-value"
                value={configValue}
                onChange={(e) => setConfigValue(e.target.value)}
                placeholder={
                  channel === "email"
                    ? "admin@example.com, soc@example.com"
                    : "https://..."
                }
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="min-severity">Minimum Severity</Label>
              <select
                id="min-severity"
                value={minSeverity}
                onChange={(e) => setMinSeverity(e.target.value)}
                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <CategoryMultiSelect
              id="add-categories"
              value={categories}
              onChange={setCategories}
            />

            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSubmit}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending && (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                )}
                <Plus className="mr-1.5 h-4 w-4" />
                Add Channel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
